import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { handleGoogleLogin } from '../../lib/auth';
import toast from 'react-hot-toast';
import { FaSpinner, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

export default function WebViewComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    completeAuth();
  }, []);

  const completeAuth = async () => {
    try {
      const url = new URL(window.location.href);
      const code = url.searchParams.get('code');
      const state = url.searchParams.get('state');
      
      if (!code) {
        throw new Error('Missing authorization code');
      }

      // Exchange the authorization code for a session
      const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
      
      if (exchangeError) {
        throw exchangeError;
      }
      
      if (!data.session) {
        throw new Error('No session received from code exchange');
      }
      
      // Process the user data using existing handleGoogleLogin function
      const user = await handleGoogleLogin();
      
      if (!user) {
        throw new Error('Failed to process user data');
      }
      
      setSuccess(true);
      toast.success('Login successful!');
      
      // Small delay to show success state, then redirect
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1500);
      
    } catch (error: any) {
      setError(error.message || 'Authentication failed');
      toast.error(error.message || 'Authentication failed');
      
      // Redirect to login after error
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 border-b border-white/10">
          <div className="text-center">
            <img 
              src="https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/amalieaispanishlogo.png"
              alt="Amalie AI"
              className="w-16 h-16 mx-auto mb-4 rounded-full"
            />
            <h2 className="text-2xl font-bold text-white">Completing Login</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          {loading && (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto">
                <FaSpinner className="w-8 h-8 text-yellow-400 animate-spin" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Processing Authentication</h3>
                <p className="text-white/70 text-sm">Please wait while we complete your login...</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-gray-400">
                  This may take a few seconds. Do not close this window.
                </p>
              </div>
            </div>
          )}

          {success && (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <FaCheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Login Successful!</h3>
                <p className="text-white/70 text-sm">Redirecting to your dashboard...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <FaExclamationTriangle className="w-8 h-8 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Authentication Failed</h3>
                <p className="text-red-200 text-sm mb-4">{error}</p>
                <p className="text-white/70 text-xs">Redirecting to login page...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}